import React, { useState, useMemo } from 'react';
import Select from '../../components/Form/Select';
import Input from '../../components/Form/Input';
import Button from '../../components/Form/Button';
import TableSkeleton from '../../components/Loading/TableSkeleton';
import { PageHeader, ErrorState } from '../../components/Common';
import TimesheetGrid from './TimesheetGrid';
import OffCrewSection from './OffCrewSection';
import { useBranches } from '../../hooks/useBranches';
import { useTimesheetState, ALL_VESSELS } from './useTimesheetState';
import { currentMonthValue, countWorkedDays } from './timesheetUtils';
import { printTimesheet } from './timesheetPrint';

const Timesheet: React.FC = () => {
  const [vesselId, setVesselId] = useState('');
  const [month, setMonth] = useState(currentMonthValue());

  const lookupQO = useMemo(() => ({ pagination: { pageSize: 200 } }), []);
  const { data: branches = [] } = useBranches(lookupQO);

  const {
    employeesLoading, error, refetch,
    selectedBranch, vesselEmployees,
    rows, handleRowChange,
    offCrewRows, handleOffCrewChange, addOffCrewRow, removeOffCrewRow,
    save, isSaving,
  } = useTimesheetState(vesselId, month, branches as any[]);

  const vesselOptions = useMemo(() => [
    { value: '', label: 'Select vessel/headquarters...' },
    { value: ALL_VESSELS, label: 'All Employees' },
    ...(branches as any[]).map((b: any) => ({
      value: b.documentId || String(b.id),
      label: `${b.name}${b.type ? ` (${b.type})` : ''}`,
    })),
  ], [branches]);

  const totalWorked = useMemo(
    () => vesselEmployees.reduce((sum: number, emp: any) => {
      const key = emp.documentId || String(emp.id);
      return sum + countWorkedDays(rows[key]?.days);
    }, 0),
    [vesselEmployees, rows]
  );

  const handlePrint = () => {
    if (!vesselId) return;
    printTimesheet({
      vesselName: vesselId === ALL_VESSELS ? 'All Vessels' : (selectedBranch?.name || '-'),
      month,
      employees: vesselEmployees,
      rows,
      offCrewRows: vesselId === ALL_VESSELS ? [] : offCrewRows,
    });
  };

  if (error) return <ErrorState message="Error loading employees." onRetry={refetch} entityName="timesheet" />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Time Sheet"
        description="Track worked days per employee on each vessel — تعيين and رفت days count as half a day"
      />

      <div className="bg-white shadow rounded-lg p-4 flex flex-wrap items-end gap-4">
        <div className="w-full sm:w-72">
          <Select label="Vessel / Headquarters" value={vesselId} onChange={(e) => setVesselId(e.target.value)} options={vesselOptions} />
        </div>
        <div className="w-full sm:w-48">
          <Input label="Month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
        </div>
        <div className="flex-1" />
        {vesselId && (
          <div className="text-sm text-gray-600 pb-2">
            {vesselEmployees.length} employees · {totalWorked} worked days
          </div>
        )}
        <Button variant="secondary" onClick={handlePrint} disabled={!vesselId}>
          Print
        </Button>
        <Button variant="primary" onClick={save} loading={isSaving} disabled={!vesselId}>
          Save Timesheet
        </Button>
      </div>

      <div className="flex flex-wrap gap-4 text-sm text-gray-600 px-1">
        <span><span className="inline-block w-5 h-5 text-center rounded border bg-green-100 text-green-700 border-green-300 font-bold mr-1">✓</span> Worked (1 day)</span>
        <span><span className="inline-block w-5 h-5 text-center rounded border bg-amber-100 text-amber-700 border-amber-300 font-bold mr-1">½</span> تعيين / رفت (half day)</span>
        <span><span className="inline-block w-5 h-5 text-center rounded border bg-gray-100 text-gray-400 border-gray-200 font-bold mr-1">✗</span> Not worked (0)</span>
      </div>

      {employeesLoading ? (
        <TableSkeleton />
      ) : vesselId ? (
        <>
          <TimesheetGrid employees={vesselEmployees} month={month} rows={rows} onChange={handleRowChange} />
          {vesselId !== ALL_VESSELS && (
            <OffCrewSection
              month={month}
              rows={offCrewRows}
              onChange={handleOffCrewChange}
              onAdd={addOffCrewRow}
              onRemove={removeOffCrewRow}
            />
          )}
        </>
      ) : (
        <div className="bg-white shadow rounded-lg text-center py-12">
          <p className="text-gray-500">Select a vessel/headquarters to show its employees</p>
        </div>
      )}
    </div>
  );
};

export default Timesheet;
